import React, { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import httpService from '../../services/httpService';
import { ApiEndpoints, Constants } from '../../constants/appConstants';
import { useExcelImportPopup } from '../Common/useExcelImportPopup';
import { useDataManagementFileUpload } from '../Common/useDataManagementFileUpload';
import { ImportTemplates } from '../../constants/appConstants';
import { createDialogOverlayStyles } from '../../utils/safeCssInjection';
import './MeasureComponent.scss';
import { Toast } from 'primereact/toast';
import DataManagementForm from '../DataManagement/DataManagementForm';
import { dataManagementFormConfig, getFormValidationErrors, normalizeFieldValues } from '../DataManagement/dataManagementFormConfig';
import DataManagementLoader from '../DataManagement/DataManagementLoader';

interface Measure {
  measureId: number;
  measureNumber: string;
  isActive: boolean;
  measureNumberFile: string;
  providerType: string;
}

const fetchMeasures = () => {
  return httpService.get(ApiEndpoints.GetAllMeasuresWithInactive);
};

interface MeasuresComponentProps {
  providerTypes: { label: string; value: string }[];
}

const MeasureComponent: React.FC<MeasuresComponentProps> = ({ providerTypes }) => {
  React.useEffect(() => {
    const cleanup = createDialogOverlayStyles('measure-component-styles');
    return cleanup;
  }, []);

  const [measures, setMeasures] = useState<Measure[]>([]);
  const { uploadFile } = useDataManagementFileUpload();

  const handleExcelUpload = async (file: File) => {
    setLoading(true);
    try {
      const fileKey = await uploadFile(file, Constants.DATA_MANAGEMENT_MODULES.MEASURES);

      await httpService.post(ApiEndpoints.BulkUploadDataManagement, {
        fileKey,
        fileName: file.name,
        module: Constants.DATA_MANAGEMENT_MODULES.MEASURES,
      });

      const response = await fetchMeasures();
      setMeasures(response.data as Measure[]);
      toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Measures uploaded successfully', life: 3000 });
    } catch (err: any) {
      let msg = err?.response?.data?.message || err?.message || "Bulk upload failed";
      toast.current?.show({ severity: 'error', summary: 'Error', detail: msg, life: 3000 });
    } finally {
      setLoading(false);
    }
  };

  const { showImport, ImportPopup } = useExcelImportPopup({
    templateUrl: ImportTemplates.measures,
    onUpload: handleExcelUpload,
    title: 'Import Measures from Excel',
  });

  const [providerTypeFilter, setProviderTypeFilter] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [selectedMeasure, setSelectedMeasure] = useState<any>(null);
  const [isAddMode, setIsAddMode] = useState(false);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [measureToDelete, setMeasureToDelete] = useState<any>(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 7;

  useEffect(() => {
    setLoading(true);
    fetchMeasures()
      .then((response: any) => {
        let data = response.data;
        if (!Array.isArray(data)) data = [];
        setMeasures(data);
        setLoading(false);
      })
      .catch((err: any) => {
        setError(`Failed to fetch measures: ${err.message}`);
        setLoading(false);
      });
  }, []);

  const toast = React.useRef<Toast>(null);
  
  const providerTypeOptions = providerTypes && providerTypes.length > 0
    ? providerTypes : Array.from(new Set(measures.map(m => m.providerType).filter(Boolean))).map(pt => ({ label: pt, value: pt }));

  let filteredMeasures = providerTypeFilter ? measures.filter(m => m.providerType === providerTypeFilter) : measures;
  if (search.trim()) {
    filteredMeasures = filteredMeasures.filter(m =>
      m.measureNumber && m.measureNumber.toLowerCase().includes(search.trim().toLowerCase())
    );
  }

  const totalPages = Math.ceil(filteredMeasures.length / rowsPerPage);
  const paginatedMeasures = filteredMeasures.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);


  // Reset page when filters change
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  return (
    <div style={{ marginTop: 24, position: 'relative', minHeight: 220 }} aria-busy={loading}>
      <DataManagementLoader visible={loading} label="Loading measures" />
      <Toast ref={toast} position="top-right" style={{ zIndex: 9999 }} />
      {error && <div style={{ color: 'red' }}>{error}</div>}
      {ImportPopup}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, gap: 16 }}>
        <div style={{ display: 'flex', gap: 12 }}>
          <Button label="Add New Measure" icon="pi pi-plus" onClick={() => {
            setSelectedMeasure({ measureId: 0, measureNumber: '', isActive: true, measureNumberFile: '', providerType: '' });
            setIsAddMode(true);
            setEditDialogVisible(true);
          }} />
          <Button label="Import" icon="pi pi-upload" className="p-button-secondary" onClick={showImport} style={{ marginLeft: 8 }} />
        </div>
        <div className="measure-search-anim">
          <input
            type="text"
            placeholder="Search by Measure Number..."
            value={search}
            onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
          />
          <span className="pi pi-search" />
        </div>
        <div className="measure-provider-type-dropdown">
          <select
            value={providerTypeFilter}
            onChange={e => { setProviderTypeFilter(e.target.value); setCurrentPage(1); }}
          >
            <option value="">All Provider Types</option>
            {providerTypeOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <span className="pi pi-angle-down" />
        </div>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
        <thead>
          <tr style={{ background: '#f0f4f8' }}>
            <th style={{ padding: 8, border: '1px solid #b0b6be', width: 60 }}>#</th>
            {/* <th style={{ padding: 8, border: '1px solid #b0b6be', width: 100 }}>Measure ID</th> */}
            <th style={{ padding: 8, border: '1px solid #b0b6be', width: 160 }}>Measure Number</th>
            <th style={{ padding: 8, border: '1px solid #b0b6be', width: 100 }}>Is Active</th>
            <th style={{ padding: 8, border: '1px solid #b0b6be', width: 180 }}>Measure Number File</th>
            <th style={{ padding: 8, border: '1px solid #b0b6be', width: 120 }}>Provider Type</th>
            <th style={{ padding: 8, border: '1px solid #b0b6be', width: 140 }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredMeasures.length === 0 ? (
            <tr>
              <td colSpan={7} style={{ textAlign: 'center', padding: 16 }}>No measures found.</td>
            </tr>
          ) : (
            paginatedMeasures.map((m, idx) => (
              <tr key={m.measureId} style={{ background: idx % 2 === 0 ? '#fff' : '#f6f8fa' }}>
                <td style={{ padding: 8, border: '1px solid #eee' }}>{(currentPage - 1) * rowsPerPage + idx + 1}</td>
                {/* <td style={{ padding: 8, border: '1px solid #eee' }}>{m.measureId}</td> */}
                <td style={{ padding: 8, border: '1px solid #eee' }}>{m.measureNumber || '-'}</td>
                <td style={{ padding: 8, border: '1px solid #eee' }}>{m.isActive ? 'Yes' : 'No'}</td>
                <td style={{ padding: 8, border: '1px solid #eee' }}>{m.measureNumberFile || '-'}</td>
                <td style={{ padding: 8, border: '1px solid #eee' }}>{m.providerType || '-'}</td>
                <td style={{ padding: 8, border: '1px solid #eee' }}>
                  <Button icon="pi pi-pencil" className="p-button-text" style={{ marginRight: 8 }} onClick={() => { setSelectedMeasure({ ...m }); setIsAddMode(false); setEditDialogVisible(true); }} />
                  <Button icon="pi pi-trash" className="p-button-text p-button-danger" onClick={() => { setMeasureToDelete(m); setDeleteDialogVisible(true); }} />
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {/* Pagination */}
      <div style={{ marginTop: 16, textAlign: 'right', color: '#888', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8 }}>
        <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} style={{ padding: '4px 10px', borderRadius: 4, border: '1px solid #ccc', background: currentPage === 1 ? '#eee' : '#fff', cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}>&lt;</button>
        <span>Page {currentPage} of {totalPages || 1}</span>
        <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages || totalPages === 0} style={{ padding: '4px 10px', borderRadius: 4, border: '1px solid #ccc', background: (currentPage === totalPages || totalPages === 0) ? '#eee' : '#fff', cursor: (currentPage === totalPages || totalPages === 0) ? 'not-allowed' : 'pointer' }}>&gt;</button>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog
        header={isAddMode ? "Add Measure" : `Edit Measure${selectedMeasure && selectedMeasure.measureNumber ? ` - ${selectedMeasure.measureNumber}` : ''}`}
        visible={editDialogVisible}
        style={{ width: '500px', maxWidth: '90vw' }}
        onHide={() => { setEditDialogVisible(false); setIsAddMode(false); }}
        footer={
          <div>
            <Button label="Cancel" onClick={() => { setEditDialogVisible(false); setIsAddMode(false); }} className="p-button-text" />
            <Button label="Save" onClick={async () => {
              const validationErrors = getFormValidationErrors(dataManagementFormConfig.measures, selectedMeasure);
              if (validationErrors.length > 0) {
                toast.current?.show({ severity: 'warn', summary: 'Fix the following', detail: validationErrors.join(', '), life: 4000 });
                return;
              }
              const payload = normalizeFieldValues(dataManagementFormConfig.measures, selectedMeasure);
              if (isAddMode) {
                try {
                  setLoading(true);
                  await httpService.post(ApiEndpoints.AddMeasure, payload);
                  const response = await fetchMeasures();
                  setMeasures(response.data as Measure[]);
                  setEditDialogVisible(false);
                  setIsAddMode(false);
                  // Success toast
                  toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Measure added successfully', life: 3000 });
                } catch (err: any) {
                  let msg = err?.response?.data?.message || err?.message || "Unknown error";
                  toast.current?.show({ severity: 'error', summary: 'Error', detail: msg, life: 3000 });
                  setTimeout(() => {
                    setEditDialogVisible(false);
                    setIsAddMode(false);
                  }, 100);
                } finally {
                  setLoading(false);
                }
              } else if (selectedMeasure) {
                try {
                  setLoading(true);
                  await httpService.put(`${ApiEndpoints.UpdateMeasure}/${selectedMeasure.measureId}`, payload);
                  const response = await fetchMeasures();
                  setMeasures(response.data as Measure[]);
                  setEditDialogVisible(false);
                  setIsAddMode(false);
                  // Success toast
                  toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Measure updated successfully', life: 3000 });
                } catch (err: any) {
                  let msg = err?.response?.data?.message || err?.message || "Unknown error";
                  toast.current?.show({ severity: 'error', summary: 'Error', detail: msg, life: 3000 });
                  setTimeout(() => {
                    setEditDialogVisible(false);
                    setIsAddMode(false);
                  }, 100);
                } finally {
                  setLoading(false);
                }
              }
            }} loading={loading} disabled={loading} autoFocus />
          </div>
        }
      >
        <MeasureEditForm editMeasure={selectedMeasure} setEditMeasure={setSelectedMeasure} providerTypesOptions={providerTypeOptions} />
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        header="Confirm Delete"
        visible={deleteDialogVisible}
        style={{ width: '350px' }}
        onHide={() => setDeleteDialogVisible(false)}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button label="Yes" autoFocus={false} onClick={() => {
              setMeasures(measures.filter(m => m.measureId !== (measureToDelete && measureToDelete.measureId)));
              setDeleteDialogVisible(false);
            }} className="p-button-danger" />
            <Button label="No" autoFocus={true} onClick={() => setDeleteDialogVisible(false)} className="p-button-secondary" />
          </div>
        }
      >
        <div style={{ fontSize: 16, margin: '16px 0' }}>
          Are you sure you want to delete this record{measureToDelete && measureToDelete.measureNumber ? `: ${measureToDelete.measureNumber}` : ''}?
        </div>
      </Dialog>
    </div>
  );
};

interface MeasureEditFormProps {
  editMeasure: any;
  setEditMeasure: (measure: any) => void;
  providerTypesOptions:{
 label: string;
 value: string;
}[];
}

const MeasureEditForm: React.FC<MeasureEditFormProps> = ({ editMeasure, setEditMeasure,providerTypesOptions }) => {
  return (
    <DataManagementForm
      fields={dataManagementFormConfig.measures}
      values={editMeasure}
      onChange={setEditMeasure}
      options={{ providerType: providerTypesOptions }}
    />
  );
};

export default MeasureComponent;